import React, { useState } from "react";
import { Container, Reveal, useCollection } from "@/components/common";
import PageHero from "@/components/PageHero";
import { getContent } from "@/lib/api";
import { X, Images } from "@phosphor-icons/react";

function AlbumModal({ album, onClose }) {
  const { data: photos, loading } = useCollection(() => getContent("gallery_images", { album_id: album.id }), [album.id]);
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-navy/95 p-4 py-10" onClick={onClose} data-testid="album-modal">
      <div className="mx-auto max-w-6xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-6 flex items-center justify-between">
          <div>
            <span className="overline text-gold">{album.category}</span>
            <h3 className="font-serif text-3xl text-white">{album.title}</h3>
          </div>
          <button onClick={onClose} data-testid="album-modal-close" className="rounded-full bg-white/10 p-2 text-white hover:bg-white/20"><X size={22} /></button>
        </div>
        {loading ? <p className="py-16 text-center text-slate-400">Loading…</p> :
          photos.length === 0 ? (
            <div className="rounded-lg border border-dashed border-white/20 p-14 text-center text-slate-400">
              <Images size={40} className="mx-auto text-gold" />
              <p className="mt-3 text-sm">No photos in this album yet. The administrator can add photos from the dashboard.</p>
            </div>
          ) : (
            <div className="columns-1 gap-4 sm:columns-2 lg:columns-3">
              {photos.map((p) => (
                <figure key={p.id} className="mb-4 break-inside-avoid overflow-hidden rounded-lg border border-white/10 bg-navy-800">
                  <img src={p.image} alt={p.caption || album.title} className="w-full object-cover" />
                  {(p.caption || p.location) && (
                    <figcaption className="p-3 text-xs text-slate-300">{p.caption}{p.location ? ` · ${p.location}` : ""}{p.date ? ` · ${p.date}` : ""}</figcaption>
                  )}
                </figure>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}

export default function Gallery() {
  const { data } = useCollection(() => getContent("gallery_albums"), []);
  const [cat, setCat] = useState("All");
  const [active, setActive] = useState(null);
  const cats = ["All", ...Array.from(new Set(data.map((a) => a.category).filter(Boolean)))];
  const shown = cat === "All" ? data : data.filter((a) => a.category === cat);

  return (
    <div data-testid="gallery-page">
      <PageHero label="Gallery" title="Moments & Milestones"
        intro="Conferences, international meetings, village camps, community outreach, awareness campaigns and professional life."
        image="https://images.pexels.com/photos/8761541/pexels-photo-8761541.jpeg" />
      <section className="bg-white py-16">
        <Container>
          <div className="mb-10 flex flex-wrap gap-2">
            {cats.map((c) => (
              <button key={c} data-testid={`gallery-filter-${c}`} onClick={() => setCat(c)}
                className={`rounded-full border px-4 py-1.5 text-xs font-medium transition-colors ${cat === c ? "border-navy bg-navy text-white" : "border-slate-300 text-navy/70 hover:border-gold"}`}>{c}</button>
            ))}
          </div>
          {shown.length === 0 ? <p className="py-16 text-center text-softgrey-600">No albums yet.</p> : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {shown.map((a, i) => (
                <Reveal key={a.id} delay={(i % 3) * 0.06}>
                  <button onClick={() => setActive(a)} data-testid={`gallery-album-${a.slug || a.id}`} className="group relative block w-full overflow-hidden rounded-lg border border-slate-200 text-left">
                    <div className="aspect-[4/3] overflow-hidden bg-softgrey">
                      {a.cover_image && <img src={a.cover_image} alt={a.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />}
                    </div>
                    <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-navy/90 via-navy/20 to-transparent p-6">
                      {a.is_demo && <span className="mb-1 w-fit rounded bg-amber-400/90 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-navy">DEMO</span>}
                      <span className="overline text-gold">{a.category}</span>
                      <h3 className="mt-1 font-serif text-2xl text-white">{a.title}</h3>
                      {a.description && <p className="mt-1 line-clamp-2 text-xs text-slate-300">{a.description}</p>}
                      <span className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-gold"><Images size={13} /> View album</span>
                    </div>
                  </button>
                </Reveal>
              ))}
            </div>
          )}
        </Container>
      </section>
      {active && <AlbumModal album={active} onClose={() => setActive(null)} />}
    </div>
  );
}
